import React, { useState, useEffect } from "react";
import env from "@beam-australia/react-env";

import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useSkeleton from "../../../../hooks/useSkeleton";

import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { SelectButton } from "primereact/selectbutton";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { classNames } from "primereact/utils";
import { Image } from "primereact/image";
import { Divider } from 'primereact/divider';

import "react-toastify/dist/ReactToastify.css";

import useApi from "../../../../hooks/useApi";
import useCRUDService from "../../../../hooks/useCRUDService";
import Searcher from "../../../../components/searcher/searcher";
import ImageUpload from "../../../../components/fileUpload/fileUpload";
import helperFormats from "../../../../utils/helperFormats";

import { atom, useAtom } from "jotai";
import { loadingState } from "../../../../states/loadingState";

const ApplicationsForm = ({ settings }) => {
  const [isLoading, setIsLoading] = useAtom(loadingState);

  const s = useSkeleton("abm");
  const api = useApi();
  const CRUDService = useCRUDService("beneficiaries");
  const clientsCRUDService = useCRUDService("clients");
  const { t } = useTranslation();

  const isEditMode = settings.formMode === "UPDATE";
  const isDeleteMode = settings.formMode === "DELETE";
  const isViewMode = settings.formMode === "VIEW";

  const {
    trigger,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      amount: 0,
      installmentsNumber: 0,
      installmentsAmount: "",
      answerDate: 0,
      status: "PENDING",
      answerDetail: "",
      beneficiary_LastName: "",
      beneficiary_Name: "",
      beneficiary_DocumentType: 0,
      beneficiary_DocumentNumber: "",
      beneficiary_Email: "",
      beneficiary_Phone: 0,
      beneficiary_Address: "",
      annualRate: 0,
      annualDefaultRate: 0,
      administrationFeeRate: 0,
      createdDate: 0,
      images: [],
    },
  });

  const [currentClient, setCurrentClient] = useState({});
  const [selectedBeneficiary, setSelectedBeneficiary] = useState();
  const [valueAmount, setValueAmount] = useState();
  const [valueInstallmentsNumber, setValueInstallmentsNumber] = useState();
  const [amountsOptions, setAmountsOptions] = useState([]);
  const [installmentsOptions, setInstallmentsOptions] = useState([]);
  const [beneficiaryId, setBeneficiaryId] = useState();
  const [applicationAnswered, SetApplicationAnswered] = useState(false);
  const [images, setImages] = useState([]);

  const [documentImage, setDocumentImage] = useState("");
  const [addressImage, setAddressImage] = useState("");
  const [paycheckImage1, setPaycheckImage1] = useState("");
  const [paycheckImage2, setPaycheckImage2] = useState("");
  const [paycheckImage3, setPaycheckImage3] = useState("");

  const [answerResult, setAnswerResult] = useState("");
  const [applicationType, setapplicationType] = useState("");
  const [creditsCurrentDebts, setCreditsCurrentDebts] = useState([]);

  const fetchDataBeneficiaries = async (
    first,
    rows,
    sortField,
    sortOrder,
    debouncedSearchTerm
  ) => {
    const columns = JSON.stringify([
      {
        name: "name",
        type: "string",
      },
      {
        name: "lastName",
        type: "string",
      },
      {
        name: "address",
        type: "string",
      },
      {
        name: "phone",
        type: "string",
      },
      {
        name: "documentNumber",
        type: "string",
      },
    ]);

    const result = await CRUDService.getEntities(
      columns,
      first,
      rows,
      sortField,
      sortOrder,
      debouncedSearchTerm
    );

    return result;
  };

  const formModeConfig = {};
  if (isEditMode) {
    formModeConfig.RejectButtonText = t("buttons.reject");
    formModeConfig.RejectButtonTextWait = `${t("buttons.updating")}...`;
    formModeConfig.ApproveButtonText = t("buttons.approve");
    formModeConfig.ApproveButtonTextWait = `${t("buttons.updating")}...`;
    formModeConfig.serverAction = async (data) => {
      await api.put(
        `${env("API_URL")}/applications/${settings.entityId}/${answerResult}`,
        data
      );
    };
  } else if (isDeleteMode) {
    formModeConfig.SubmitButtonText = t("buttons.delete");
    formModeConfig.SubmitButtonTextWait = `${t("buttons.deleting")}...`;
    formModeConfig.serverAction = async () => {
      await settings.CRUDService.deleteEntity(settings.entityId);
    };
  } else if (isViewMode) {
    // Modo solo lectura, sin acciones del servidor
    formModeConfig.serverAction = null;
  } else {
    formModeConfig.SubmitButtonText = t("buttons.create");
    formModeConfig.SubmitButtonTextWait = `${t("buttons.creating")}...`;
    formModeConfig.serverAction = async (data) => {
      data.amount = valueAmount;
      data.installmentsNumber = valueInstallmentsNumber;
      data.beneficiary_DocumentType = 1;
      data.beneficiaryId = beneficiaryId;
      data.beneficiaryDocumentImage = documentImage;
      data.beneficiaryAddressImage = addressImage;
      data.beneficiarySalaryReceiptImage1 = paycheckImage1;
      data.beneficiarySalaryReceiptImage2 = paycheckImage2;
      data.beneficiarySalaryReceiptImage3 = paycheckImage3;
      await settings.CRUDService.createEntity(data, "", "");
    };
  }

  const getInstallmentAmount = async () => {
    if (valueAmount && valueInstallmentsNumber) {
      setIsLoading(true);

      try {
        const response = await api.get(
          `${env("API_URL")}/applications/GetInstallmentAmount`,
          {
            params: {
              amount: valueAmount,
              installments: valueInstallmentsNumber,
            },
          }
        );

        setValue("installmentsAmount", response.data);
        trigger("installmentsAmount");
      } catch (error) {
        settings.toast.current.show({
          severity: "error",
          summary: "Error",
          detail: t("errors.general"),
          life: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    setIsLoading(true);
    getInstallmentAmount();
    setIsLoading(false);
  }, [valueAmount, valueInstallmentsNumber]);

  useEffect(() => {
    if (selectedBeneficiary && selectedBeneficiary.name) {
      setValue(
        "beneficiary_DocumentNumber",
        selectedBeneficiary.documentNumber
      ); // Usa setValue para actualizar el valor en react-hook-form
      trigger("beneficiary_DocumentNumber"); // Trigger la validación

      setValue("beneficiary_Name", selectedBeneficiary.name);
      trigger("beneficiary_Name");

      setValue("beneficiary_LastName", selectedBeneficiary.lastName);
      trigger("beneficiary_LastName");

      setValue("beneficiary_Email", selectedBeneficiary.email);
      trigger("beneficiary_Email");

      setValue("beneficiary_Phone", selectedBeneficiary.phone);
      trigger("beneficiary_Phone");

      setValue("beneficiary_Address", selectedBeneficiary.address);
      trigger("beneficiary_Address");

      setBeneficiaryId(selectedBeneficiary.id);
    }
  }, [selectedBeneficiary, setValue, trigger]);

  useEffect(() => {
    const fetchData = async () => {
      // Se lee el cliente para obtener su configuración
      const client = await clientsCRUDService.getEntity(
        localStorage.getItem("clientId"),
        "base"
      );

      console.log("1");

      setCurrentClient(client);

      if (isEditMode || isDeleteMode || isViewMode) {
        // Si se está en modo edición, borrado o visualización se cargan los datos leidos del objeto applications
        try {
          const data = await settings.CRUDService.getEntity(settings.entityId);

          console.log("DATA APPLICATIONS FORM", data);

          setapplicationType(data.type);
          setCreditsCurrentDebts(data.creditsCurrentDebts);

          setImages(JSON.parse(data.imagesUris));

          for (const key in data) {
            if (key === "amount") {
              setValueAmount(key["amount"]);
            }
            if (key === "installmentsNumber") {
              setValueInstallmentsNumber(data["installmentsNumber"]);
            }
            setValue(key, data[key]);
          }

          SetApplicationAnswered(helperFormats.isDate(data.answerDate));
        } catch (error) {
          console.error(t("forms.messages.operation.error"), error);
        }
      } else {
        // Si se está en modo creación se cargan datos del cliente
        client.clientsInstallmentsAmountsCollection.forEach((element) => {
          setAmountsOptions((oldAmountOptions) => [
            ...oldAmountOptions,
            element.installmentAmount,
          ]);
        });

        client.clientsInstallmentsNumbersCollection.forEach((element) => {
          setInstallmentsOptions((oldInstallmentsOptions) => [
            ...oldInstallmentsOptions,
            element.installmentNumber,
          ]);
        });

        setValue("annualRate", client.clientsSettings.annualRate);
        setValue("annualDefaultRate", client.clientsSettings.annualDefaultRate);
        setValue(
          "administrationFeeRate",
          client.clientsSettings.administrationFeeRate
        );
      }
    };

    fetchData();
  }, [settings.entityId, setValue, settings.formMode]);

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      await formModeConfig.serverAction(data);
      settings.hideDialog();

      settings.toast.current.show({
        severity: "success",
        summary: t("forms.messages.titles.success"),
        detail: t("forms.messages.operation.success"),
        life: 3000,
      });
    } catch (error) {
      console.log(error);
      if (error.response?.status === 405) {
        settings.toast.current.show({
          severity: "error",
          summary: t("forms.messages.titles.error"),
          detail: t("entities.applications.errors.alreadyAnswered"),
          life: 3000,
        });
      } else if (error.response?.status === 409) {
        settings.toast.current.show({
          severity: "error",
          summary: t("forms.messages.titles.error"),
          detail: t("entities.applications.errors.withholdingInProgress"),
          life: 3000,
        });
      } else {
        settings.toast.current.show({
          severity: "error",
          summary: t("forms.messages.titles.error"),
          detail: t("forms.messages.operation.error"),
          life: 3000,
        });
      }

    } finally {
      setIsLoading(false);
    }
  };

  const ApproveApplication = () => {
    setAnswerResult("approve");
    handleSubmit();
  };

  const RejectApplication = () => {
    setAnswerResult("reject");
    handleSubmit();
  };

  const handleImageError = (event, e) => {
    event.target.src = "/images/no_image.png";
    setIsZoomEnabled(false);
  };

  const [isZoomEnabled, setIsZoomEnabled] = useState(true);

  const CurrentOpenCredits = () => {
    console.log(creditsCurrentDebts);
    return (
      <React.Fragment>
        {creditsCurrentDebts.map((credit, index) => (
          <React.Fragment>
            <Divider />
            <div className={s.get("form.field")}>
              <div className={s.get("form.field.label")}>
                <label>
                  {`Renovación crédito ${credit.creditCode} `}
                </label>
              </div>

            </div>
            <div className={s.get("form.field")}>
              <div className={s.get("form.field.label")}>
                <label>
                  Cuotas restantes
                </label>
              </div>
              <div className={s.get("form.field.value")}>
                <div className={s.get("form.field.value.control")}>
                  <InputText
                    id="remainingInstallments"
                    value={credit.remainingInstallments}
                    readOnly
                    className="mt-3"
                  />
                </div>
              </div>
            </div>
            <div className={s.get("form.field")}>
              <div className={s.get("form.field.label")}>
                <label>
                  Deuda actual
                </label>
              </div>
              <div className={s.get("form.field.value")}>
                <div className={s.get("form.field.value.control")}>
                  <InputNumber
                    id="capitalOwed"
                    value={(credit.capitalOwed + credit.interestsOwed + credit.taxesOwed).toFixed(2)}
                    readOnly
                    className="mt-3"
                    mode="decimal"
                    locale="de-DE"
                  />
                </div>
              </div>
            </div>
            <div className={s.get("form.field")}>
              <div className={s.get("form.field.label")}>
                <label>
                  Mora actual
                </label>
              </div>
              <div className={s.get("form.field.value")}>
                <div className={s.get("form.field.value.control")}>
                  <InputNumber
                    id="currentArrearsOwed"
                    value={(credit.arrearsCapitalOwed + credit.arrearsInterestOwed + credit.arrearsTaxesOwed).toFixed(2)}
                    readOnly
                    className="mt-3"
                    mode="decimal"
                    locale="de-DE"
                  />
                </div>
              </div>
            </div>
            <div className={s.get("form.field")}>
              <div className={s.get("form.field.label")}>
                <label>
                  Capital pendiente
                </label>
              </div>
              <div className={s.get("form.field.value")}>
                <div className={s.get("form.field.value.control")}>
                  <InputNumber
                    id="arrearsCapitalOwedfutureInstallmentsRemainingCapital"
                    value={credit.futureInstallmentsRemainingCapital.toFixed(2)}
                    readOnly
                    className="mt-3"
                    mode="decimal"
                    locale="de-DE"
                  />
                </div>
              </div>
            </div>
            <div className={s.get("form.field")}>
              <div className={s.get("form.field.label")}>
                <label>
                  Deuda total
                </label>
              </div>
              <div className={s.get("form.field.value")}>
                <div className={s.get("form.field.value.control")}>
                  <InputNumber
                    id="totalDebt"
                    value={credit.totalDebt.toFixed(2)}
                    readOnly
                    className="mt-3"
                    mode="decimal"
                    locale="de-DE"
                  />
                </div>
              </div>
            </div>
          </React.Fragment>
        ))}
        <Divider />
      </React.Fragment>
    );
  };

  const ShowImages = () => {
    console.log(images);
    return (
      <div className="imagenes-contenedor">
        {images.map((url, index) => (
          <Image
            src={url}
            zoomSrc={url}
            alt={`Imagen ${index + 1}`}
            width="80"
            height="60"
            key={index}
            preview={isZoomEnabled}
            onError={handleImageError}
          />
        ))}
      </div>
    );
  };

  // Se obtiene el nombre del mes seleccionado para la leyenda de los recibos
  // Si el día es menor o igual a 10 se pide uno menos
  const getMonthName = (previous = 0) => {
    const date = new Date();
    if (date.getDay <= 10) {
      previous--;
    }
    date.setMonth(date.getMonth() + previous);
    return date.toLocaleString("es-ES", { month: "long" });
  };

  const getFormErrorMessage = (error) => {
    return error && <small className="p-error">{error.message}</small>;
  };

  return (
    <React.Fragment>
      <div className={s.get()}>
        <div className="card flex justify-center"></div>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={`${s.get("form")} ${settings.formMode !== "DELETE" ? "abm-create-form" : ""}`}
        >
          <Controller
            name="amount"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.applications.amount"),
              }),
            }}
            render={({ field }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="amount">
                    {t("entities.applications.amount")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.amount)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    {isEditMode || isDeleteMode || isViewMode ? (
                      <InputNumber
                        id={field.name}
                        value={field.value ?? ""}
                        onValueChange={(e) => field.onChange(e.value)}
                        mode="decimal"
                        locale="de-DE"
                        inputClassName={[
                          classNames({ "p-invalid": errors.amount }),
                        ]}
                        readOnly
                      />
                    ) : (
                      <SelectButton
                        id={field.name}
                        options={amountsOptions || []}
                        value={valueAmount}
                        onChange={(e) => {
                          if (e.value) setValueAmount(e.value);
                        }}
                        className={"amount"}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          />

          <Controller
            name="installmentsNumber"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.applications.installmentsNumber"),
              }),
            }}
            render={({ field }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="installmentsNumber">
                    {t("entities.applications.installmentsNumber")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.installmentsNumber)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    {isEditMode || isDeleteMode || isViewMode ? (
                      <InputNumber
                        id={field.name}
                        value={field.value ?? ""}
                        onValueChange={(e) => field.onChange(e.value)}
                        mode="decimal"
                        locale="de-DE"
                        inputClassName={[
                          classNames({
                            "p-invalid": errors.installmentsNumber,
                          }),
                        ]}
                        readOnly
                      />
                    ) : (
                      <SelectButton
                        id={field.name}
                        options={installmentsOptions}
                        value={valueInstallmentsNumber}
                        onChange={(e) => {
                          if (e.value) setValueInstallmentsNumber(e.value);
                        }}
                        className={[
                          "installments",
                          classNames({ "p-invalid": errors.amount }),
                        ]}
                        disabled={settings.formMode == "DELETE"}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="installmentsAmount"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.applications.installmentsAmount"),
              }),
            }}
            render={({ field }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="installmentsAmount">
                    {t("entities.applications.installmentsAmount")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.installmentsAmount)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputNumber
                      id={field.name}
                      value={field.value ?? ""}
                      onValueChange={(e) => field.onChange(e.value)}
                      mode="decimal"
                      locale="de-DE"
                      min={1}
                      minFractionDigits={2}
                      maxFractionDigits={2}
                      inputClassName={[
                        classNames({ "p-invalid": errors.installmentsAmount }),
                      ]}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            )}
          />
          {applicationType == "RENEWAL" && (CurrentOpenCredits())}
          {applicationAnswered && (
            <React.Fragment>
              <Controller
                name="answerDate"
                control={control}
                rules={{}}
                render={({ field }) => (
                  <div className={s.get("form.field")}>
                    <div className={s.get("form.field.label")}>
                      <label htmlFor="answerDate">
                        {t("entities.applications.answerDate")}
                      </label>
                    </div>
                    <div className={s.get("form.field.value")}>
                      <div className={s.get("form.field.value.error")}>
                        {getFormErrorMessage(errors.answerDate)}
                      </div>
                      <div className={s.get("form.field.value.control")}>
                        <InputText
                          id="answerDate"
                          value={helperFormats.formatDate(
                            new Date(field.value)
                          )}
                          maxLength={100}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                )}
              />
              <Controller
                name="answer"
                control={control}
                rules={{}}
                render={({ field }) => (
                  <div className={s.get("form.field")}>
                    <div className={s.get("form.field.label")}>
                      <label htmlFor="answer">
                        {t("entities.applications.answer")}
                      </label>
                    </div>
                    <div className={s.get("form.field.value")}>
                      <div className={s.get("form.field.value.error")}>
                        {getFormErrorMessage(errors.answer)}
                      </div>
                      <div className={s.get("form.field.value.control")}>
                        <InputText
                          id="answerDate"
                          value={
                            field.value
                              ? "SOLICITUD APROBADA"
                              : "SOLICITUD RECHAZADA"
                          }
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                )}
              />
              <Controller
                name="answerDetail"
                control={control}
                rules={{
                  maxLength: {
                    value: 255,
                    message: t("forms.validation.maxLength", {
                      entity: t("entities.applications.answerDetail"),
                      maxLength: 255,
                    }),
                  },
                }}
                render={({ field, fieldState }) => (
                  <div className={s.get("form.field")}>
                    <div className={s.get("form.field.label")}>
                      <label htmlFor="answerDetail">
                        {t("entities.applications.answerDetail")}
                      </label>
                    </div>
                    <div className={s.get("form.field.value")}>
                      <div className={s.get("form.field.value.error")}>
                        {getFormErrorMessage(errors.answerDetail)}
                      </div>
                      <div className={s.get("form.field.value.control")}>
                        <InputText
                          id="answerDetail"
                          value={field.value ?? ""}
                          maxLength={100}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                )}
              />
            </React.Fragment>
          )}
          {!isEditMode && !isViewMode && (
            <Searcher
              settings={{
                entityName: "beneficiaries",
                fieldName: "beneficiaries",
                selectedEntity: selectedBeneficiary,
                setSelectedEntity: setSelectedBeneficiary,
                defaultSortField: "name",
                fetchData: fetchDataBeneficiaries,
                columns: [
                  <Column
                    field="name"
                    header={t("entities.beneficiaries.name")}
                    sortable
                    key="id"
                  ></Column>,
                  <Column
                    field="lastName"
                    header={t("entities.beneficiaries.lastName")}
                    sortable
                    key="id"
                  ></Column>,
                  <Column
                    field="address"
                    header={t("entities.beneficiaries.address")}
                    sortable
                    key="id"
                  ></Column>,
                  <Column
                    field="phone"
                    header={t("entities.beneficiaries.phone")}
                    sortable
                    key="id"
                  ></Column>,
                  <Column
                    field="documentNumber"
                    header={t("entities.beneficiaries.documentNumber")}
                    sortable
                    key="id"
                  ></Column>,
                ],
              }}
            />
          )}
          <Controller
            name="beneficiary_Name"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.applications.beneficiary_Name"),
              }),
              maxLength: {
                value: 100,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.applications.beneficiary_Name"),
                  maxLength: 100,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="beneficiary_Name">
                    {t("entities.applications.beneficiary_Name")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.beneficiary_Name)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="beneficiary_Name"
                      value={selectedBeneficiary?.name ?? field.value ?? ""}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="beneficiary_LastName"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.applications.beneficiary_LastName"),
              }),
              maxLength: {
                value: 100,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.applications.beneficiary_LastName"),
                  maxLength: 100,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="beneficiary_LastName">
                    {t("entities.applications.beneficiary_LastName")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.beneficiary_LastName)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="beneficiary_LastName"
                      value={selectedBeneficiary?.lastName ?? field.value ?? ""}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="beneficiary_DocumentNumber"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.applications.beneficiary_DocumentNumber"),
              }),
              maxLength: {
                value: 50,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.applications.beneficiary_DocumentNumber"),
                  maxLength: 50,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="beneficiary_DocumentNumber">
                    {t("entities.applications.beneficiary_DocumentNumber")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.beneficiary_DocumentNumber)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="beneficiary_DocumentNumber"
                      value={
                        selectedBeneficiary?.documentNumber ?? field.value ?? ""
                      }
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="beneficiary_Email"
            control={control}
            rules={{
              maxLength: {
                value: 256,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.applications.beneficiary_Email"),
                  maxLength: 256,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="beneficiary_Email">
                    {t("entities.applications.beneficiary_Email")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.beneficiary_Email)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="beneficiary_Email"
                      value={selectedBeneficiary?.email ?? field.value ?? ""}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="beneficiary_Phone"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.applications.beneficiary_Phone"),
              }),
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="beneficiary_Phone">
                    {t("entities.applications.beneficiary_Phone")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.beneficiary_Phone)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="beneficiary_Phone"
                      value={selectedBeneficiary?.phone ?? field.value ?? ""}
                      maxLength={50}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="beneficiary_Address"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.applications.beneficiary_Address"),
              }),
              maxLength: {
                value: 255,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.applications.beneficiary_Address"),
                  maxLength: 255,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="beneficiary_Address">
                    {t("entities.applications.beneficiary_Address")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.beneficiary_Address)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="beneficiary_Address"
                      value={selectedBeneficiary?.address ?? field.value ?? ""}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="annualRate"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.applications.annualRate"),
              }),
            }}
            render={({ field }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="annualRate">
                    {t("entities.applications.annualRate")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.annualRate)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id={field.name}
                      value={`${field.value * 100}%` ?? ""}
                      inputClassName={[
                        classNames({ "p-invalid": errors.annualRate }),
                      ]}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="annualDefaultRate"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.applications.annualDefaultRate"),
              }),
            }}
            render={({ field }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="annualDefaultRate">
                    {t("entities.applications.annualDefaultRate")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.annualDefaultRate)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id={field.name}
                      value={`${field.value * 100}%` ?? ""}
                      inputClassName={[
                        classNames({ "p-invalid": errors.annualDefaultRate }),
                      ]}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="administrationFeeRate"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.applications.administrationFeeRate"),
              }),
            }}
            render={({ field }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="administrationFeeRate">
                    {t("entities.applications.administrationFeeRate")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.administrationFeeRate)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id={field.name}
                      value={`${field.value * 100}%` ?? ""}
                      inputClassName={[
                        classNames({
                          "p-invalid": errors.administrationFeeRate,
                        }),
                      ]}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            )}
          />

          {(isEditMode || isDeleteMode || isViewMode) && (
            <Controller
              name="createdDate"
              control={control}
              rules={{
                required: t("forms.validation.required", {
                  entity: t("entities.applications.createdDate"),
                }),
              }}
              render={({ field, fieldState }) => (
                <div className={s.get("form.field")}>
                  <div className={s.get("form.field.label")}>
                    <label htmlFor="createdDate">
                      {t("entities.applications.createdDate")}
                    </label>
                  </div>
                  <div className={s.get("form.field.value")}>
                    <div className={s.get("form.field.value.error")}>
                      {getFormErrorMessage(errors.createdDate)}
                    </div>
                    <div className={s.get("form.field.value.control")}>
                      <InputText
                        id="createdDate"
                        value={
                          helperFormats.formatDate(new Date(field.value)) ?? ""
                        }
                        maxLength={100}
                        className={[
                          "form-control",
                          classNames({
                            "p-invalid": fieldState.invalid,
                          }),
                        ]}
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              )}
            />
          )}

          <Controller
            name="images"
            control={control}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="images">
                    {t("entities.applications.images")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.images)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    {isEditMode || isDeleteMode || isViewMode ? (
                      <ShowImages />
                    ) : (
                      <React.Fragment>
                        <div className="flex justify-content-between align-content-end">
                          <div className="field">
                            <label htmlFor="beneficiaryDocumentImage">
                              {t(
                                "entities.applications.imagesDescription.beneficiaryDocument"
                              )}
                            </label>
                            <div>
                              <ImageUpload
                                setImage={setDocumentImage}
                                auto="true"
                                chooseLabel="Seleccionar imagen de documento"
                                emptyText="Arrastra y suelta la imagen de tu documento aquí"
                              ></ImageUpload>
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-content-between align-content-end">
                          <div className="field">
                            <label htmlFor="beneficiaryAddress">
                              {t(
                                "entities.applications.imagesDescription.beneficiaryAddress"
                              )}
                            </label>
                            <div>
                              <ImageUpload
                                setImage={setAddressImage}
                                auto="true"
                                chooseLabel="Seleccionar imagen de constancia de domicilio"
                                emptyText="Arrastra y suelta la imagen de tu constancia de domicilio aquí"
                              ></ImageUpload>
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-content-between align-content-end">
                          <div className="field">
                            <label htmlFor="beneficiaryAddress">
                              {t(
                                "entities.applications.imagesDescription.beneficiaryAddress"
                              )}
                            </label>
                            <div>
                              <ImageUpload
                                setImage={setPaycheckImage1}
                                auto="true"
                                chooseLabel={`Seleccionar imagen de recibo de sueldo de ${getMonthName(
                                  -3
                                )}`}
                                emptyText={`Arrastra y suelta la imagen de tu recibo de sueldo de ${getMonthName(
                                  -3
                                )} aquí`}
                              ></ImageUpload>
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-content-between align-content-end">
                          <div className="field">
                            <label htmlFor="beneficiaryAddress">
                              {t(
                                "entities.applications.imagesDescription.beneficiaryAddress"
                              )}
                            </label>
                            <div>
                              <ImageUpload
                                setImage={setPaycheckImage2}
                                auto="true"
                                chooseLabel={`Seleccionar imagen de recibo de sueldo de ${getMonthName(
                                  -2
                                )}`}
                                emptyText={`Arrastra y suelta la imagen de tu recibo de sueldo de ${getMonthName(
                                  -2
                                )} aquí`}
                              ></ImageUpload>
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-content-between align-content-end">
                          <div className="field">
                            <label htmlFor="beneficiaryAddress">
                              {t(
                                "entities.applications.imagesDescription.beneficiaryAddress"
                              )}
                            </label>
                            <div>
                              <ImageUpload
                                setImage={setPaycheckImage3}
                                auto="true"
                                chooseLabel={`Seleccionar imagen de recibo de sueldo de ${getMonthName(-1)}`}
                                emptyText={`Arrastra y suelta la imagen de tu recibo de sueldo de ${getMonthName(-1)} aquí`}
                              ></ImageUpload>
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    )}
                  </div>
                </div>
              </div>
            )}
          />

          <div className="flex justify-content-between mt-6 mb-2">
            <Button
              label={t("buttons.cancel")}
              icon="pi pi-times"
              className="p-button-text btn-secondary-crud"
              onClick={() => settings.hideDialog("CANCEL")}
              type="button"
              disabled={isLoading}
            />
            <div className="flex justify-content-endflex-grow-1">
              {!applicationAnswered && isEditMode && (
                <React.Fragment>
                  <Button
                    label={
                      isLoading
                        ? formModeConfig.RejectButtonTextWait
                        : formModeConfig.RejectButtonText
                    }
                    icon="pi pi-arrow-circle-down"
                    className="border-red-700 bg-red-700 hover:bg-red-800"
                    onClick={RejectApplication}
                    disabled={isLoading}
                  />
                  <Button
                    label={
                      isLoading
                        ? formModeConfig.ApproveButtonTextWait
                        : formModeConfig.ApproveButtonText
                    }
                    icon="pi pi-arrow-circle-up"
                    className=" btn btn-primary btn-primary-crud ml-5"
                    onClick={ApproveApplication}
                    disabled={isLoading}
                  />
                </React.Fragment>
              )}
              {!(isDeleteMode || isEditMode || isViewMode) && (
                <Button
                  label={
                    isLoading
                      ? formModeConfig.SubmitButtonTextWait
                      : formModeConfig.SubmitButtonText
                  }
                  icon="pi pi-check"
                  className=" btn btn-primary btn-primary-crud"
                  type="submit"
                  disabled={isLoading}
                />
              )}
            </div>
          </div>
        </form>
      </div>
    </React.Fragment>
  );
};

export default ApplicationsForm;
